// src/mobile_pages/const/OrderComplete_new.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Save,
  FileCheck,
  FileText,
  User,
  Link as LinkIcon,
  Paperclip,
  ChevronRight,
  ClipboardList
} from "lucide-react";

import { getCompletionReportDetail, createCompletionReport, updateCompletionReport } from "../../api/const";
import { createConstMeta, updateConstMeta } from "../../api/const";

// 모바일 전용 컴포넌트
import MobileWorkOrderSelect from "./components/MobileWorkOrderSelect";
import MobilePhotoBoardSelect from "./components/MobilePhotoBoardSelect";
import MobilePhotoBoardReadonly from "./components/MobilePhotoBoardReadonly";

// 기존 컴포넌트 재사용
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import ReferenceModal from "../../components/approval/ReferenceModal";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";

// 요약 카드 컴포넌트 (파일 내부 정의 or 별도 분리 가능)
const MobileDocSummaryCard = ({ title, items }) => {
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 mb-4">
      <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
        <ClipboardList size={16} className="text-blue-600" />
        {title}
      </h3>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs">
            <span className="text-blue-500 font-medium min-w-[80px]">{item.label}</span>
            <span className="text-slate-700">{item.value || "-"}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function OrderCompleteNew() {
  const { id: projectId, dns } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const attachmentBoxRef = useRef(null);

  const isEditMode = Boolean(dns);
  const [saving, setSaving] = useState(false);

  // 폼 상태
  const [form, setForm] = useState({
    title: "",
    workorder_dns: null,
    workorder_title: "",
    workorder_project: "", // 요약용
    workorder_date: "", // 요약용
    photoboard: null,
    approver_id: null,
    approver_name: "",
  });

  // 모달 상태
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [approverIds, setApproverIds] = useState([]);

  // 섹션 확장 상태
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    relation: true,
    content: true,
    attachments: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 데이터 로드
  useEffect(() => {
    if (!dns) return;

    const loadData = async () => {
      try {
        const res = await getCompletionReportDetail(dns);
        const data = res.data || res;

        setForm({
          title: data.title || "",
          workorder_dns: data.meta?.workorder || null,
          workorder_title: data.workorder_title || "-",
          workorder_project: "-", // API에서 바로 안오면 별도 조회 필요할 수 있음
          workorder_date: "-",
          photoboard: data.meta?.photoboard || null,
          approver_id: data.approval?.approver_id || null,
          approver_name: data.approval?.approver_name || "",
        });

        if (data.approval?.approver_id) {
          setApproverIds([data.approval.approver_id]);
        }

        // TinyMCE 초기화
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setContent(data.meta?.detail || "");
          }
        }, 300);

        // 첨부파일 로드
        if (data.attachments && attachmentBoxRef.current) {
          const initialFiles = data.attachments.map(f => ({
            ...f,
            _type: "uploaded",
            name: f.filename || f.name,
          }));
          attachmentBoxRef.current.setItems(initialFiles);
        }

      } catch (err) {
        console.error("데이터 로드 실패:", err);
      }
    };
    loadData();
  }, [dns]);

  // 저장 핸들러
  const handleSave = async () => {
    // 1. 유효성 검사
    if (!form.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }
    if (!form.approver_id) {
      alert("승인자를 선택해주세요.");
      return;
    }
    if (!form.workorder_dns) {
      alert("작업지시서를 선택해주세요.");
      return;
    }

    setSaving(true);
    try {
      const detailHtml = editorRef.current?.getContent() || "";

      // Payload 구성
      const payload = {
        project: projectId,
        module: "const",
        doc_type: "ordercomplete",
        values: {
          title: form.title,
          doc_type: "ordercomplete",
          dns: dns || null,
          confirmer: form.approver_id,
          confirmer_name: form.approver_name,
          workorder: form.workorder_dns,
          meta: {
            detail: detailHtml,
            workorder: form.workorder_dns,
            photoboard: form.photoboard,
          },
        },
      };

      let currentDns = dns;
      if (isEditMode) {
        await updateConstMeta(dns, payload);
      } else {
        const created = await createConstMeta(payload);
        currentDns = created.dns || created.id;
      }

      // 첨부파일 저장
      if (attachmentBoxRef.current && currentDns) {
        await attachmentBoxRef.current.save(currentDns);
      }

      alert("저장되었습니다.");
      navigate(`/project/${projectId}/const/ordercomplete`);
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 헤더 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 완료보고서</p>
          <h1 className="text-xl font-bold text-slate-900">
            {isEditMode ? "완료보고서 수정" : "완료보고서 작성"}
          </h1>
        </header>

        {/* 작업지시서 요약 카드 (선택된 경우만 표시) */}
        {form.workorder_dns && (
          <MobileDocSummaryCard
            title="작업지시서 요약"
            items={[
              { label: "문서번호", value: form.workorder_dns },
              { label: "지시제목", value: form.workorder_title },
              { label: "연결상태", value: "연결됨" },
            ]}
          />
        )}


        {/* 폼 섹션들 */}
        <div className="space-y-3">
          {/* 1. 기본 정보 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.basic}
            onToggle={() => toggleSection("basic")}
            icon={<FileCheck size={16} className="text-blue-500" />}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="제목을 입력하세요"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-blue-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  승인자 <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowApproverModal(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.99] transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                      <User size={18} className="text-slate-500" />
                    </div>
                    <div className="text-left">
                      {form.approver_name ? (
                        <>
                          <p className="text-[13px] font-medium text-slate-800">
                            {form.approver_name}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            승인 담당자
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-[13px] font-medium text-slate-500">
                            승인자를 선택하세요
                          </p>
                          <p className="text-[11px] text-slate-400">
                            탭하여 선택
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </button>
              </div>
            </div>
          </Section>

          {/* 2. 연결 정보 */}
          <Section
            title="연결 정보"
            expanded={expandedSections.relation}
            onToggle={() => toggleSection("relation")}
            icon={<LinkIcon size={16} className="text-purple-500" />}
          >
            <div className="space-y-4">
              {/* 작업지시서 선택 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  작업지시서 <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setOpenOrderModal(true)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.99] transition-all"
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center">
                      <ClipboardList size={18} className="text-indigo-500" />
                    </div>
                    <div className="text-left min-w-0">
                      {form.workorder_dns ? (
                        <>
                          <p className="text-[13px] font-medium text-slate-800 truncate">
                            {form.workorder_title || form.workorder_dns}
                          </p>
                          <p className="text-[11px] text-slate-400">
                            연결됨
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-[13px] font-medium text-slate-500">
                            작업지시서를 선택하세요
                          </p>
                          <p className="text-[11px] text-slate-400">
                            탭하여 목록 조회
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400 flex-shrink-0" />
                </button>
              </div>

              {/* 사진대지 선택 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  사진대지
                </label>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setOpenPhotoModal(true)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.99] transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center">
                        <Paperclip size={18} className="text-orange-500" />
                      </div>
                      <div className="text-left">
                        {form.photoboard ? (
                          <>
                            <p className="text-[13px] font-medium text-slate-800">
                              사진대지 연결됨
                            </p>
                            <p className="text-[11px] text-slate-400">
                              탭하여 변경
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-[13px] font-medium text-slate-500">
                              사진대지 선택 (선택)
                            </p>
                            <p className="text-[11px] text-slate-400">
                              탭하여 선택
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-slate-400" />
                  </button>

                  {form.photoboard && (
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                      <MobilePhotoBoardReadonly photoboardId={form.photoboard} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* 3. 상세 내용 */}
          <Section
            title="상세 내용"
            expanded={expandedSections.content}
            onToggle={() => toggleSection("content")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
              <TinyMCEeditor height={400} editorRef={editorRef} />
            </div>
          </Section>

          {/* 4. 첨부 자료 */}
          <Section
            title="첨부 자료"
            expanded={expandedSections.attachments}
            onToggle={() => toggleSection("attachments")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentBoxRef} />
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => {
              if (window.confirm("작성 중인 내용이 저장되지 않습니다. 취소하시겠습니까?")) {
                navigate(-1);
              }
            }}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={18} />
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>

      {/* 모바일 전용 모달 사용 */}
      <MobileWorkOrderSelect
        isOpen={openOrderModal}
        onClose={() => setOpenOrderModal(false)}
        onSelect={(row) => {
          setForm((p) => ({
            ...p,
            workorder_dns: row.dns,
            workorder_title: row.title,
            // 추가 정보가 있다면 set
          }));
        }}
      />

      <MobilePhotoBoardSelect
        isOpen={openPhotoModal}
        onClose={() => setOpenPhotoModal(false)}
        onSelect={(board) => {
          setForm((p) => ({ ...p, photoboard: board.id }));
        }}
      />

      <ReferenceModal
        isOpen={showApproverModal}
        onClose={() => setShowApproverModal(false)}
        selectedIds={approverIds}
        name="승인자 선택"
        onChange={(ids) => {
          const only = ids.slice(-1);
          setApproverIds(only);
        }}
        onSave={(ids, users) => {
          const u = users?.[0];
          if (u) {
            setForm((p) => ({
              ...p,
              approver_id: u.id,
              approver_name: u.last_name + u.first_name,
            }));
          }
          return true;
        }}
      />
    </div>
  );
}

function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}